import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@trillion/ui/components/ui/dialog";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Label } from "@trillion/ui/components/ui/label";
import { Badge } from "@trillion/ui/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@trillion/ui/components/ui/select";
import { SecretValueInput } from "@trillion/ui/components/admin/secret-value-input";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { firstFieldErrors, zodToFieldErrors } from "@trillion/validation";
import {
  createOAuthClientRequestSchema,
  redirectUriSchema,
} from "@/validation/create-oauth-client-request";
import { updateOAuthClientRequestSchema } from "@/validation/update-oauth-client-request";
import { adminApi } from "@/lib/admin-api";
import type { OAuthClient } from "@/types";

interface OAuthClientDialogProps {
  /** undefined → create mode; present → edit mode (client_id read-only, secret write-only). */
  editing?: OAuthClient;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Refetch the list after a successful create/update. */
  onSaved: () => void;
}

type ClientType = "public" | "confidential";

// Render the first error matching the base key OR a `${field}.` prefix. zodToFieldErrors keys array-item
// issues as `redirect_uris.0` (path.join(".")), which a base-key-only lookup would miss.
function fieldError(errors: Record<string, string>, field: string): string | undefined {
  if (errors[field]) return errors[field];
  const prefix = `${field}.`;
  for (const [k, v] of Object.entries(errors)) {
    if (v && k.startsWith(prefix)) return v;
  }
  return undefined;
}

// Mirror the server's fuller redirect_uri policy (OAuthClientService.ValidateRedirectUris) for add-time UX:
// no URL fragment, and http only for loopback. The scheme (http/https) + URL shape is already covered by the
// shared redirectUriSchema; the server stays authoritative.
function redirectUriPolicyError(uri: string): string | undefined {
  let url: URL;
  try {
    url = new URL(uri);
  } catch {
    return "Each redirect_uri must be a valid URL";
  }
  if (url.hash) return "redirect_uri must not contain a fragment";
  const loopback = ["localhost", "127.0.0.1", "[::1]", "::1"];
  if (url.protocol === "http:" && !loopback.includes(url.hostname)) {
    return "http is allowed only for loopback (use https)";
  }
  return undefined;
}

// Single create/edit dialog for OAuth clients (mirrors conduit's settings-dialog). client_id is fixed on
// edit; client_secret is write-only (never seeded from the server). redirect_uris/permissions are in-memory
// badge array editors (removal render mirrors invite-user-form; the free-text Input + Add + Enter-to-add is
// new). Cross-field rules (confidential⇒secret, public⇒no secret) are gated client-side for UX but the
// server is authoritative.
export function OAuthClientDialog({ editing, open, onOpenChange, onSaved }: OAuthClientDialogProps) {
  const isEdit = editing !== undefined;
  const [clientId, setClientId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [applicationType, setApplicationType] = useState<"web" | "native">("web");
  const [clientType, setClientType] = useState<ClientType>("public");
  const [slug, setSlug] = useState("");
  const [redirectUris, setRedirectUris] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [clientSecret, setClientSecret] = useState("");
  const [newUri, setNewUri] = useState("");
  const [newPermission, setNewPermission] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Seed on open: create → blank; edit → from the row, EXCEPT client_secret (write-only — never seeded).
  useEffect(() => {
    if (!open) return;
    setErrors({});
    setNewUri("");
    setNewPermission("");
    setClientSecret("");
    if (editing) {
      setClientId(editing.client_id);
      setDisplayName(editing.display_name ?? "");
      setClientType((editing.client_type as ClientType) ?? "public");
      setApplicationType(editing.application_type);
      setSlug(editing.slug ?? "");
      setRedirectUris(editing.redirect_uris ?? []);
      setPermissions(editing.permissions ?? []);
    } else {
      setClientId("");
      setDisplayName("");
      setClientType("public");
      setApplicationType("web");
      setSlug("");
      setRedirectUris([]);
      setPermissions([]);
    }
  }, [open, editing]);

  const clearError = (field: string) => setErrors((e) => ({ ...e, [field]: "" }));

  // Wipe all form state — including any entered secret plaintext — on every close path.
  function resetForm() {
    setClientId("");
    setDisplayName("");
    setClientType("public");
    setApplicationType("web");
    setSlug("");
    setRedirectUris([]);
    setPermissions([]);
    setClientSecret("");
    setNewUri("");
    setNewPermission("");
    setErrors({});
  }

  function handleClose() {
    resetForm();
    onOpenChange(false);
  }

  // Validate a pending redirect URI at add-time (mirrors the schema's own z.url()) so the stored array holds
  // only valid URLs — the submit-time safeParse then never emits an indexed redirect_uris.<n> error.
  function addUri() {
    const uri = newUri.trim();
    if (!uri) return;
    // Scheme (http/https) + URL shape via the shared redirectUriSchema — identical to the server boundary.
    const parsed = redirectUriSchema.safeParse(uri);
    if (!parsed.success) {
      setErrors((e) => ({
        ...e,
        redirect_uris: parsed.error.issues[0]?.message ?? "Invalid URL",
      }));
      return;
    }
    // Fuller policy (no fragment; http only for loopback) — mirrors the server's ValidateRedirectUris for UX.
    const policyError = redirectUriPolicyError(uri);
    if (policyError) {
      setErrors((e) => ({ ...e, redirect_uris: policyError }));
      return;
    }
    if (!redirectUris.includes(uri)) setRedirectUris((prev) => [...prev, uri]);
    setNewUri("");
    clearError("redirect_uris");
  }

  function removeUri(uri: string) {
    setRedirectUris((prev) => prev.filter((u) => u !== uri));
  }

  function addPermission() {
    const perm = newPermission.trim();
    if (!perm) return;
    if (!permissions.includes(perm)) setPermissions((prev) => [...prev, perm]);
    setNewPermission("");
    clearError("permissions");
  }

  function removePermission(perm: string) {
    setPermissions((prev) => prev.filter((p) => p !== perm));
  }

  async function handleSubmit() {
    // Client-side confidential-secret gate (UX only; the server is authoritative). A confidential client
    // needs a secret on create or on a public→confidential transition; a public client must carry none.
    const becomingConfidential =
      clientType === "confidential" && (!isEdit || editing!.client_type !== "confidential");
    if (becomingConfidential && !clientSecret) {
      setErrors((e) => ({
        ...e,
        client_secret: "A client secret is required for confidential clients",
      }));
      return;
    }
    if (clientType === "public" && clientSecret) {
      setErrors((e) => ({ ...e, client_secret: "Public clients must not have a client secret" }));
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit && editing) {
        // Blank secret ⇒ omit (leave the stored hash unchanged). null ⇒ leave field unchanged; [] ⇒ clear.
        const payload = {
          client_type: clientType,
          application_type: applicationType,
          display_name: displayName.trim() || null,
          slug: slug.trim() || null,
          redirect_uris: redirectUris,
          permissions,
          ...(clientSecret ? { client_secret: clientSecret } : {}),
        };
        const result = updateOAuthClientRequestSchema.safeParse(payload);
        if (!result.success) {
          setErrors(firstFieldErrors(zodToFieldErrors(result.error)));
          return;
        }
        await adminApi.oauthClients.update(editing.client_id, result.data);
        toast.success(`Updated "${editing.client_id}"`);
      } else {
        const payload = {
          client_id: clientId.trim(),
          client_type: clientType,
          application_type: applicationType,
          display_name: displayName.trim() || null,
          slug: slug.trim() || null,
          redirect_uris: redirectUris,
          permissions: permissions.length ? permissions : null,
          client_secret: clientSecret || null,
        };
        const result = createOAuthClientRequestSchema.safeParse(payload);
        if (!result.success) {
          setErrors(firstFieldErrors(zodToFieldErrors(result.error)));
          return;
        }
        await adminApi.oauthClients.create(result.data);
        toast.success(`Created "${result.data.client_id}"`);
      }
      handleClose();
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save application");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : handleClose())}>
      {/* This form is taller than conduit's settings dialog (7 fields + array editors + secret); bound the
          height and scroll internally so the footer stays reachable on short viewports. */}
      <DialogContent className="max-h-[calc(100vh-2rem)] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit application" : "Add application"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the OAuth client. The client ID is fixed; leave the secret blank to keep the current one."
              : "Register a new OAuth client (application)."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* Self-lockout warning — the passport client carries the SSO callback passport-admin logs in through */}
          {isEdit && editing?.client_id === "passport" && (
            <div className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
              This is the Passport login client. Removing its <code>…/auth/sso/callback</code> redirect URI
              locks admins out of passport-admin — the server refuses such an edit.
            </div>
          )}

          {/* client_id — editable on create, read-only on edit */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="client_id">Client ID</Label>
            <Input
              id="client_id"
              value={clientId}
              placeholder="my-app"
              disabled={isEdit}
              onChange={(e) => {
                setClientId(e.target.value);
                clearError("client_id");
              }}
            />
            {errors.client_id && <p className="text-xs text-destructive">{errors.client_id}</p>}
          </div>

          {/* display_name */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="display_name">Display name</Label>
            <Input
              id="display_name"
              value={displayName}
              placeholder="My App"
              onChange={(e) => {
                setDisplayName(e.target.value);
                clearError("display_name");
              }}
            />
            {errors.display_name && (
              <p className="text-xs text-destructive">{errors.display_name}</p>
            )}
          </div>

          {/* client_type */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="client_type">Client type</Label>
            <Select
              value={clientType}
              onValueChange={(v) => {
                const t = v as ClientType;
                setClientType(t);
                if (t === "public") setClientSecret(""); // public clients hold no secret
                clearError("client_secret");
              }}
            >
              <SelectTrigger id="client_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public (PKCE)</SelectItem>
                <SelectItem value="confidential">Confidential (secret)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="application_type">Application type</Label>
            <Select
              value={applicationType}
              onValueChange={(value) => {
                setApplicationType(value as "web" | "native");
                clearError("application_type");
              }}
            >
              <SelectTrigger id="application_type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="web">Web</SelectItem>
                <SelectItem value="native">Native</SelectItem>
              </SelectContent>
            </Select>
            {errors.application_type && <p className="text-xs text-destructive">{errors.application_type}</p>}
          </div>

          {/* slug — optional role-assignable product key */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={slug}
              placeholder="optional — role-assignable product key"
              onChange={(e) => {
                setSlug(e.target.value);
                clearError("slug");
              }}
            />
            {errors.slug && <p className="text-xs text-destructive">{errors.slug}</p>}
          </div>

          {/* redirect_uris — badge array editor (add-time z.url() validation) */}
          <div className="flex flex-col gap-2">
            <Label>Redirect URIs</Label>
            {redirectUris.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {redirectUris.map((uri) => (
                  <Badge key={uri} variant="secondary" className="max-w-full gap-1 pr-1">
                    <span className="truncate">{uri}</span>
                    <button
                      type="button"
                      onClick={() => removeUri(uri)}
                      className="rounded-sm hover:bg-muted-foreground/20"
                      aria-label={`Remove ${uri}`}
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <Input
                value={newUri}
                placeholder="https://example.com/callback"
                onChange={(e) => {
                  setNewUri(e.target.value);
                  clearError("redirect_uris");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addUri();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addUri}
                disabled={!newUri.trim()}
              >
                <Plus className="mr-1 size-4" />
                Add
              </Button>
            </div>
            {fieldError(errors, "redirect_uris") && (
              <p className="text-xs text-destructive">{fieldError(errors, "redirect_uris")}</p>
            )}
          </div>

          {/* permissions — badge array editor (free-text) */}
          <div className="flex flex-col gap-2">
            <Label>Permissions</Label>
            {permissions.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {permissions.map((perm) => (
                  <Badge key={perm} variant="secondary" className="max-w-full gap-1 pr-1">
                    <span className="truncate">{perm}</span>
                    <button
                      type="button"
                      onClick={() => removePermission(perm)}
                      className="rounded-sm hover:bg-muted-foreground/20"
                      aria-label={`Remove ${perm}`}
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <Input
                value={newPermission}
                placeholder="e.g. ept:token"
                onChange={(e) => {
                  setNewPermission(e.target.value);
                  clearError("permissions");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addPermission();
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPermission}
                disabled={!newPermission.trim()}
              >
                <Plus className="mr-1 size-4" />
                Add
              </Button>
            </div>
            {fieldError(errors, "permissions") && (
              <p className="text-xs text-destructive">{fieldError(errors, "permissions")}</p>
            )}
          </div>

          {/* client_secret — write-only masked; only for confidential clients */}
          {clientType === "confidential" && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="client_secret">Client secret</Label>
              <SecretValueInput
                id="client_secret"
                value={clientSecret}
                placeholder={
                  isEdit && editing!.secret_is_set
                    ? "•••• set — enter a new value to replace"
                    : "client secret"
                }
                onChange={(v) => {
                  setClientSecret(v);
                  clearError("client_secret");
                }}
              />
              {isEdit && editing!.secret_is_set && (
                <p className="text-xs text-muted-foreground">
                  The current secret is never displayed. Entering a value replaces it.
                </p>
              )}
              {errors.client_secret && (
                <p className="text-xs text-destructive">{errors.client_secret}</p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting && <Loader2 className="size-4 animate-spin" />}
            {isEdit ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
